/*
 * Detalle de la devolución.
 *
 * Contiene componentes para las interacciones con los estados de la devolución.
 * Muestra información de la orden a la que pertenece la devolución, productos y circuitos.
 *
 * Created on Mon Apr 17 2023 by José Luis Chávez Ayala.
 *
 * Copyright (c) 2023 Turn My App.
 */

<template>
  <div>

    <!-- #region::Skeleton zone -->
    <template v-if="loadingRefund">
      <RefundDetailsSkeleton />
    </template>
    <!-- #endregion::Skeleton zone -->

    <template v-else>
      <div class="mb-2">

        <!-- #region::Refund options -->
        <RefundDetailsOptions
          class="mb-1"
          @reload-refund="loadRefundData"
        />
        <!-- #endregion::Refund options -->

        <RefundOrderData />
        <RefundExtraData />

        <RefundProductsDetails />
        <RefundCircuitsDetails class="mt-2" />
        <RefundTotalDetails />

        <RefundPaymentsHistory
          v-if="canShowPaymentsHistory && !loadingRefund"
          @reload-refund="loadRefundData"
        />

        <RefundRejectComment />
      </div>
    </template>

  </div>
</template>

<script>
import { mapActions } from 'vuex'

import RefundOrderData from '@/modules/store/refunds/components/RefundOrderData.vue'
import RefundExtraData from '@/modules/store/refunds/components/RefundExtraData.vue'
import RefundTotalDetails from '@/modules/store/refunds/components/RefundTotalDetails.vue'
import RefundRejectComment from '@/modules/store/refunds/components/RefundRejectComment.vue'
import RefundDetailsOptions from '@/modules/store/refunds/components/RefundDetailsOptions.vue'
import RefundPaymentsHistory from '@/modules/store/refunds/components/RefundPaymentsHistory.vue'
import RefundDetailsSkeleton from '@/modules/store/refunds/components/RefundDetailsSkeleton.vue'
import getError from '@/helpers/ErrorsHandler'

export default {
  components: {
    RefundOrderData,
    RefundExtraData,
    RefundTotalDetails,
    RefundRejectComment,
    RefundDetailsOptions,
    RefundPaymentsHistory,
    RefundDetailsSkeleton,
    RefundProductsDetails: () => import('@/modules/store/refunds/components/RefundProducsDetails.vue'),
    RefundCircuitsDetails: () => import('@/modules/store/refunds/components/RefundCircuitsDetails.vue'),
  },
  props: {
    /**
     * Identificador de la orden
     */
    id: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      loadingRefund: false,
    }
  },
  computed: {
    canShowPaymentsHistory() {
      return this.$ability.can('read refund', 'Devolution')
    },
  },

  /**
  * Hook de creación de componente
  *
  * @summary Llama a la carga del registro de una devolución
  * @exception Retorna a la lista de devoluciones.
  */
  async created() {
    try {
      this.loadingRefund = true
      await this.loadRefundData()
    } catch (error) {
      this.showToast('Error de validación', getError(error), 'danger')
      this.$router.push({ name: 'orders-home' })
    } finally {
      this.$swal.close()
      this.loadingRefund = false
    }
  },

  /**
  * Hook de la destrucción del componente
  *
  * @summary Limpia la información del componente cargada en el state management
  */
  beforeDestroy() {
    this.clearData()
  },

  methods: {
    ...mapActions({
      clearData: 'refunds/clearData',
      loadRefund: 'refunds/loadRefund',
      loadRefundPayments: 'refunds/loadRefundPayments',
      setOriginProductRoute: 'products/setOriginProductRoute',
    }),

    /**
    * Carga la información de la orden
    *
    * @summary Establece la información de la orden a partir del identificador
    * y establece la ruta para los productos
    * @exception Si el producto no existe redirige a la página de no content.
    */
    async loadRefundData() {
      try {
        this.$swal({
          title: 'Cargando...',
          allowOutsideClick: false,
        })
        this.$swal.showLoading()
        await this.loadRefund(this.id)

        if (this.canShowPaymentsHistory) {
          await this.loadRefundPayments(this.id)
        }

        this.setOriginProductRoute({
          name: this.$route.name,
          params: {
            id: this.id,
          },
        })

        this.$swal.close()
      } catch (error) {
        if (error.response.status === 404) {
          this.$router.push({ name: 'error-404' })
        }
      }
    },
  },
}
</script>
